import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { Location } from "../../../types/location";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { cardSurfaceClass, cardTitleTextClass } from "@/components/common/surfaceClasses";
import { TrainingRoleplayPanel } from "./TrainingRoleplayPanel";
import { TrainingReviewQueuePanel } from "./TrainingReviewQueuePanel";
import { TrainingContactsPanel } from "./TrainingContactsPanel";

// `scenarios` was removed when training authoring re-homed to the
// Manager-bucket `trainingAuthor` mode per
// docs/design/admin_split_and_entitlements.md §5 + §7.1. Trainers now
// edit scenarios from Manager → Training Authoring; this Sales-bucket
// Training mode is consumer-facing (roleplay, ask, contacts, reviews).
export type TrainingTab = "roleplay" | "ask" | "contacts" | "reviews";

type TrainingPanelTopSectionProps = {
  trainingTab: TrainingTab;
  setTrainingTab: Dispatch<SetStateAction<TrainingTab>>;
  showReviewsTab: boolean;
  salespersonName?: string;
  locationName?: string;
  scopedOrganizationId: string;
  location: Location | null;
  kbLocationId: string | null;
  contactsPresetQuery?: string | null;
  askPanel: ReactNode;
};

function buildTabs(opts: { showReviewsTab: boolean }): { id: TrainingTab; label: string; fireflyTargetId?: string }[] {
  const tabs: { id: TrainingTab; label: string; fireflyTargetId?: string }[] = [
    { id: "roleplay", label: "Roleplay", fireflyTargetId: "training-roleplay-tab" },
    { id: "ask", label: "Ask & policies", fireflyTargetId: "training-ask-tab" },
    { id: "contacts", label: "Contacts" },
  ];
  if (opts.showReviewsTab) tabs.push({ id: "reviews", label: "Reviews" });
  return tabs;
}

export function TrainingPanelTopSection(props: TrainingPanelTopSectionProps) {
  const tabs = buildTabs({ showReviewsTab: props.showReviewsTab });
  const activePanel =
    props.trainingTab === "roleplay" ? (
      <TrainingRoleplayPanel organizationId={props.scopedOrganizationId} location={props.location} />
    ) : props.trainingTab === "reviews" ? (
      <TrainingReviewQueuePanel organizationId={props.scopedOrganizationId} />
    ) : props.trainingTab === "contacts" ? (
      <TrainingContactsPanel organizationId={props.scopedOrganizationId} locationId={props.kbLocationId} locationName={props.locationName} presetQuery={props.contactsPresetQuery ?? null} />
    ) : (
      props.askPanel
    );

  // Mobile (<lg): horizontal tab bar inside the header card, active panel
  // below — unchanged. Desktop (lg:+): tab bar promotes to a sticky 220px
  // left rail and the active panel fills the right column. Per
  // docs/planning/desktop_layout_pass.md §4.1.1 row 7.
  return (
    <>
      <div className={`${cardSurfaceClass} p-5`}>
        <h2 className={`text-2xl font-bold ${cardTitleTextClass}`}>Training & Policies</h2>
        <p className="text-sm text-gray-600 mt-1 dark:text-zinc-400">
          Roleplay with an AI customer, ask the knowledge base, or browse the scoped contacts directory.
          {props.salespersonName ? ` (${props.salespersonName.split(" ")[0]} @ ${props.locationName})` : ""}
        </p>
        <div className="mt-4 inline-flex flex-wrap rounded-xl border border-gray-200 p-0.5 bg-gray-50 gap-0.5 lg:hidden dark:border-zinc-700 dark:bg-zinc-800/50">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              {...(t.fireflyTargetId ? { [FIREFLY_TARGET_ATTR]: t.fireflyTargetId } : {})}
              className={"px-4 py-2 text-sm font-semibold rounded-lg " + (props.trainingTab === t.id ? "bg-white shadow-sm text-gray-900 dark:bg-zinc-900 dark:text-zinc-100" : "text-gray-600 dark:text-zinc-400")}
              onClick={() => props.setTrainingTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-4 lg:items-start">
        <nav className="hidden lg:block lg:sticky lg:top-4" aria-label="Training sub-navigation">
          <div className={`flex flex-col gap-1 ${cardSurfaceClass} p-2`}>
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                {...(t.fireflyTargetId ? { [FIREFLY_TARGET_ATTR]: t.fireflyTargetId } : {})}
                className={
                  "w-full text-left px-4 py-2 text-sm font-semibold rounded-lg transition " +
                  (props.trainingTab === t.id
                    ? "bg-gray-100 text-gray-900 dark:bg-zinc-800 dark:text-zinc-100"
                    : "text-gray-600 hover:bg-gray-50 dark:text-zinc-400 dark:hover:bg-zinc-800/60")
                }
                onClick={() => props.setTrainingTab(t.id)}
                aria-current={props.trainingTab === t.id ? "page" : undefined}
              >
                {t.label}
              </button>
            ))}
          </div>
        </nav>
        <div className="min-w-0">{activePanel}</div>
      </div>
    </>
  );
}
